import { useEffect, useRef, useState } from "react";
import {
  CheckCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  List,
  Pencil,
  Send,
  Trash2,
  X,
} from "lucide-react";
import type { CommentReply, ReaderComment } from "../lib/annotationStore";
import { useKeyboardInset } from "../hooks/useKeyboardInset";

type Props = {
  comments: ReaderComment[];
  activeId: string;
  /** Local author label used to gate edit (default «من») */
  currentAuthor?: string;
  onNavigate: (id: string) => void;
  onClose: () => void;
  onOpenAll?: () => void;
  onReply?: (id: string, body: string) => void;
  onDelete?: (id: string) => void;
  onDeleteReply?: (commentId: string, replyId: string) => void;
  onResolve?: (id: string) => void;
  onEdit?: (id: string, body: string) => void;
  onEditReply?: (commentId: string, replyId: string, body: string) => void;
  onResolveAll?: () => void;
  onDeleteAll?: () => void;
};

function formatDate(iso?: string): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("fa-IR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function toPersianDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]!);
}

function isAuthor(author: string | undefined, current: string): boolean {
  const a = (author || "").trim();
  const c = current.trim();
  if (!a || !c) return false;
  return a === c || a === "من" || a.toLowerCase() === "me";
}

type ThreadEntry =
  | { kind: "root"; comment: ReaderComment }
  | { kind: "reply"; commentId: string; reply: CommentReply };

/** Focused comment viewer with thread reply / resolve / delete / edit. */
export function CommentFocusSheet({
  comments,
  activeId,
  currentAuthor = "من",
  onNavigate,
  onClose,
  onOpenAll,
  onReply,
  onDelete,
  onDeleteReply,
  onResolve,
  onEdit,
  onEditReply,
  onResolveAll,
  onDeleteAll,
}: Props) {
  const visible = comments.filter((c) => !c.deleted);
  const index = Math.max(
    0,
    visible.findIndex((c) => c.word_id === activeId)
  );
  const comment = visible[index] ?? visible[0];
  const total = visible.length;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;
  const [replyText, setReplyText] = useState("");
  const [editingRoot, setEditingRoot] = useState(false);
  const [editRootText, setEditRootText] = useState("");
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyText, setEditReplyText] = useState("");
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const composeRef = useRef<HTMLDivElement>(null);
  const keyboardInset = useKeyboardInset();

  useEffect(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    setReplyText("");
    setEditingRoot(false);
    setEditingReplyId(null);
  }, [activeId]);

  // Keep reply composer visible above the soft keyboard
  useEffect(() => {
    if (keyboardInset <= 0) return;
    const t = window.setTimeout(() => {
      composeRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }, 60);
    return () => window.clearTimeout(t);
  }, [keyboardInset, replyText]);

  if (!comment) return null;

  const replies: CommentReply[] = comment.replies ?? [];
  const canEditRoot = isAuthor(comment.author, currentAuthor);
  const bottomPx = Math.max(0, keyboardInset);

  const threadEntries: ThreadEntry[] = [
    { kind: "root", comment },
    ...replies.map((reply) => ({
      kind: "reply" as const,
      commentId: comment.word_id,
      reply,
    })),
  ];

  const submitReply = () => {
    if (!onReply || !replyText.trim()) return;
    onReply(comment.word_id, replyText.trim());
    setReplyText("");
  };

  return (
    <div
      className="comment-focus-backdrop"
      role="presentation"
      onClick={onClose}
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => {
        if ((e.target as HTMLElement).closest("button, a, input, textarea")) {
          return;
        }
        e.preventDefault();
      }}
    >
      <div
        className={`comment-focus-sheet${comment.resolved ? " resolved" : ""}`}
        role="dialog"
        aria-label="یادداشت"
        aria-modal="true"
        style={{
          bottom: bottomPx,
          top: "auto",
          paddingBottom:
            bottomPx > 0
              ? "0.75rem"
              : "calc(0.85rem + var(--safe-bottom))",
          maxHeight:
            bottomPx > 0
              ? `calc(100dvh - ${bottomPx + 16}px)`
              : "min(72vh, 36rem)",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="comment-focus-handle" aria-hidden />
        <div className="comment-focus-header comment-focus-header-stack">
          <div className="comment-focus-header-top">
            <div className="comment-focus-header-main">
              <div className="comment-focus-author">یادداشت</div>
              {comment.resolved && (
                <span className="resolved-badge">برطرف شده</span>
              )}
            </div>
            <div className="comment-focus-header-actions">
              {onResolveAll && (
                <button
                  type="button"
                  className="icon-btn compact"
                  aria-label="برطرف‌کردن همه"
                  title="برطرف‌کردن همه"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onResolveAll}
                >
                  <CheckCheck size={15} />
                </button>
              )}
              {onDeleteAll && (
                <button
                  type="button"
                  className="icon-btn compact danger"
                  aria-label="حذف همه"
                  title="حذف همه"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onDeleteAll();
                    onClose();
                  }}
                >
                  <Trash2 size={15} />
                </button>
              )}
              {onOpenAll && (
                <button
                  type="button"
                  className="comment-focus-all-inline"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onOpenAll}
                >
                  <List size={14} />
                  همه
                </button>
              )}
              <button
                type="button"
                className="icon-btn"
                aria-label="بستن"
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClose}
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {comment.quote ? (
            <div className="comment-quote comment-quote-full">
              {comment.quote}
            </div>
          ) : null}
        </div>

        <div className="comment-thread">
          {threadEntries.map((entry) => {
            if (entry.kind === "root") {
              const c = entry.comment;
              const editing = editingRoot;
              return (
                <div key={`root-${c.word_id}`} className="comment-thread-item">
                  <div className="comment-quote-row thread-meta">
                    <div className="comment-meta">
                      <span className="comment-author-name">
                        {c.author || "—"}
                      </span>
                      <span>{formatDate(c.date)}</span>
                    </div>
                    <div
                      className="comment-action-icons"
                      role="group"
                      aria-label="اقدامات"
                    >
                      {onResolve && !c.resolved && (
                        <button
                          type="button"
                          className="icon-btn compact"
                          aria-label="برطرف شد"
                          title="برطرف شد"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onResolve(c.word_id)}
                        >
                          <CheckCircle2 size={16} />
                        </button>
                      )}
                      {canEditRoot && onEdit && (
                        <button
                          type="button"
                          className="icon-btn compact"
                          aria-label="ویرایش"
                          title="ویرایش"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEditingRoot(true);
                            setEditRootText(c.body || "");
                          }}
                        >
                          <Pencil size={15} />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          className="icon-btn compact danger"
                          aria-label="حذف"
                          title="حذف"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onDelete(c.word_id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                  {editing ? (
                    <div className="comment-edit-box">
                      <textarea
                        dir="rtl"
                        rows={3}
                        value={editRootText}
                        onChange={(e) => setEditRootText(e.target.value)}
                      />
                      <div className="comment-edit-actions">
                        <button
                          type="button"
                          className="icon-btn compact"
                          onClick={() => setEditingRoot(false)}
                        >
                          انصراف
                        </button>
                        <button
                          type="button"
                          className="icon-btn compact primary"
                          disabled={!editRootText.trim()}
                          onClick={() => {
                            if (!onEdit || !editRootText.trim()) return;
                            onEdit(c.word_id, editRootText.trim());
                            setEditingRoot(false);
                          }}
                        >
                          ذخیره
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="comment-body">{c.body || "—"}</div>
                  )}
                </div>
              );
            }

            const r = entry.reply;
            const canEditReply = isAuthor(r.author, currentAuthor);
            const editing = editingReplyId === r.id;
            return (
              <div key={r.id} className="comment-thread-item">
                <div className="comment-quote-row thread-meta">
                  <div className="comment-meta">
                    <span className="comment-author-name">
                      {r.author || "—"}
                    </span>
                    <span>{formatDate(r.date)}</span>
                  </div>
                  <div className="comment-action-icons">
                    {canEditReply && onEditReply && (
                      <button
                        type="button"
                        className="icon-btn compact"
                        aria-label="ویرایش پاسخ"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setEditingReplyId(r.id);
                          setEditReplyText(r.body);
                        }}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canEditReply && onDeleteReply && (
                      <button
                        type="button"
                        className="icon-btn compact danger"
                        aria-label="حذف پاسخ"
                        title="حذف پاسخ"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onDeleteReply(comment.word_id, r.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {editing ? (
                  <div className="comment-edit-box">
                    <textarea
                      dir="rtl"
                      rows={2}
                      value={editReplyText}
                      onChange={(e) => setEditReplyText(e.target.value)}
                    />
                    <div className="comment-edit-actions">
                      <button
                        type="button"
                        className="icon-btn compact"
                        onClick={() => setEditingReplyId(null)}
                      >
                        انصراف
                      </button>
                      <button
                        type="button"
                        className="icon-btn compact primary"
                        disabled={!editReplyText.trim()}
                        onClick={() => {
                          if (!onEditReply || !editReplyText.trim()) return;
                          onEditReply(
                            comment.word_id,
                            r.id,
                            editReplyText.trim()
                          );
                          setEditingReplyId(null);
                        }}
                      >
                        ذخیره
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="comment-body">{r.body}</div>
                )}
              </div>
            );
          })}
        </div>

        {!comment.resolved && onReply && (
          <div className="comment-reply-compose" ref={composeRef}>
            <textarea
              ref={replyRef}
              dir="rtl"
              rows={2}
              placeholder="پاسخ…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onFocus={() => {
                window.setTimeout(() => {
                  composeRef.current?.scrollIntoView({
                    block: "nearest",
                    behavior: "smooth",
                  });
                }, 120);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && replyText.trim()) {
                  e.preventDefault();
                  submitReply();
                }
              }}
            />
            <button
              type="button"
              className="icon-btn compact primary"
              disabled={!replyText.trim()}
              aria-label="ارسال پاسخ"
              onClick={submitReply}
            >
              <Send size={15} />
            </button>
          </div>
        )}

        {total > 1 && (
          <div className="comment-focus-nav" dir="rtl">
            <button
              type="button"
              className="icon-btn"
              disabled={!hasPrev}
              aria-label="یادداشت قبلی"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const prev = visible[index - 1];
                if (prev) onNavigate(prev.word_id);
              }}
            >
              <ChevronRight size={16} />
              قبلی
            </button>
            <span className="comment-focus-index">
              {toPersianDigits(index + 1)} از {toPersianDigits(total)}
            </span>
            <button
              type="button"
              className="icon-btn"
              disabled={!hasNext}
              aria-label="یادداشت بعدی"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const next = visible[index + 1];
                if (next) onNavigate(next.word_id);
              }}
            >
              بعدی
              <ChevronLeft size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
